export interface ICurrentSupplier {
	displayPreferences: DisplayPreferences;
	primaryContact: PrimaryContact;
	prop65Contact: Prop65Contact;
	supplierLocation: SupplierLocation;
}

export class PrimaryContact {
	primaryContactName: string;
	primaryContactTitle: string;
	primaryContactEmail: string;
	primaryContactPhone: string;
	primaryUsername: string;

	constructor(supplier?: ISupplierDTO) {
		this.primaryContactName = supplier?.primaryContactName;
		this.primaryContactTitle = supplier?.primaryContactTitle;
		this.primaryContactEmail = supplier?.primaryContactEmail;
		this.primaryContactPhone = supplier?.primaryContactPhone;
		this.primaryUsername = supplier?.primaryUsername;
	}

	setPrimaryContactName(value: string): void {
		this.primaryContactName = value;
	}

	setPrimaryContactTitle(value: string): void {
		this.primaryContactTitle = value;
	}

	setPrimaryContactEmail(value: string): void {
		this.primaryContactEmail = value;
	}

	setPrimaryContactPhone(value: string): void {
		this.primaryContactPhone = value;
	}

	setPrimaryUsername(value: string): void {
		this.primaryUsername = value;
	}
}

export class Prop65Contact {
	prop65ContactName: string;
	prop65ContactTitle: string;
	prop65ContactEmail: string;
	prop65ContactPhone: string;
	prop65Username: string;
	prop65ContactEmailTransfer: string;

	constructor(supplier: ISupplierDTO) {
		this.prop65ContactName = supplier.prop65ContactName;
		this.prop65ContactTitle = supplier.prop65ContactTitle;
		this.prop65ContactEmail = supplier.prop65ContactEmail;
		this.prop65ContactPhone = supplier.prop65ContactPhone;
		this.prop65Username = supplier.prop65Username;
	}

	setProp65ContactName(value: string): void {
		this.prop65ContactName = value;
	}

	setProp65ContactTitle(value: string): void {
		this.prop65ContactTitle = value;
	}

	setProp65ContactEmail(value: string): void {
		this.prop65ContactEmail = value;
	}

	setProp65ContactPhone(value: string): void {
		this.prop65ContactPhone = value;
	}

	setProp65Username(value: string): void {
		this.prop65Username = value;
	}

	setProp65ContactEmailTransfer(value: string): void {
		this.prop65ContactEmailTransfer = value;
	}
}

export class SupplierLocation {
	supplierAddress1: string;
	supplierAddress2: string;
	supplierCity: string;
	supplierFax: string;
	supplierPhone: string;
	supplierState: string;
	supplierZip: string;
	supplierCountry: string;

	constructor(supplier: ISupplierDTO) {
		this.supplierAddress1 = supplier.supplierAddress1;
		this.supplierAddress2 = supplier.supplierAddress2;
		this.supplierCity = supplier.supplierCity;
		this.supplierFax = supplier.supplierFax;
		this.supplierPhone = supplier.supplierPhone;
		this.supplierState = supplier.supplierState;
		this.supplierZip = supplier.supplierZip;
		this.supplierCountry = supplier.supplierCountry;
	}

	setSupplierAddress1(value: string): void {
		this.supplierAddress1 = value;
	}

	setSupplierAddress2(value: string): void {
		this.supplierAddress2 = value;
	}

	setSupplierCity(value: string): void {
		this.supplierCity = value;
	}

	setSupplierFax(value: string): void {
		this.supplierFax = value;
	}

	setSupplierPhone(value: string): void {
		this.supplierPhone = value;
	}

	setSupplierState(value: string): void {
		this.supplierState = value;
	}

	setSupplierZip(value: string): void {
		this.supplierZip = value;
	}

	setSupplierCountry(value: string): void {
		this.supplierCountry = value;
	}
}

export class DisplayPreferences {
	allowNoOnProductWarning: boolean;
	allowSellInCalifornia: boolean;
	allowUseShortWarning: boolean;

	constructor(supplier: ISupplierDTO) {
		this.allowNoOnProductWarning = supplier.allowNoOnProductWarning;
		this.allowSellInCalifornia = supplier.allowSellInCalifornia;
		this.allowUseShortWarning = supplier.allowUseShortWarning;
	}

	setDisplayNoOnProductWarning(value: boolean): void {
		this.allowNoOnProductWarning = value;
	}

	setDisplaySellInCalifornia(value: boolean): void {
		this.allowSellInCalifornia = value;
	}

	setDisplayUseShortWarning(value: boolean): void {
		this.allowUseShortWarning = value;
	}
}

export class CurrentSupplier implements ICurrentSupplier {
	displayPreferences: DisplayPreferences;
	primaryContact: PrimaryContact;
	prop65Contact: Prop65Contact;
	supplierLocation: SupplierLocation;
	supplierId: string;
	supplierName: string;
	websiteURL: string;
	updated: string;
	updatedBy: string;
	hasItems: boolean;
	hasProfileCompleted: boolean;
	constructor(supplier: ISupplierDTO) {
		this.displayPreferences = new DisplayPreferences(supplier);
		this.primaryContact = new PrimaryContact(supplier);
		this.prop65Contact = new Prop65Contact(supplier);
		this.supplierLocation = new SupplierLocation(supplier);
		this.updated = supplier.updated;
		this.updatedBy = supplier.updatedBy;
		this.hasItems = supplier.hasItems;
		this.supplierId = supplier.supplierId;
		this.supplierName = supplier.supplierName;
		this.websiteURL = supplier.websiteURL;
		this.hasProfileCompleted = this.verifyProfileCompleted();
	}

	markProfileCompleted(): void {
		this.hasProfileCompleted = this.verifyProfileCompleted();
	}

	verifyProfileCompleted(): boolean {
		return !!(
			this.primaryContact.primaryContactName &&
			this.primaryContact.primaryContactPhone &&
			this.primaryContact.primaryContactTitle &&
			this.supplierName &&
			this.supplierLocation.supplierPhone &&
			this.supplierLocation.supplierAddress1 &&
			this.supplierLocation.supplierCity &&
			this.supplierLocation.supplierZip &&
			this.supplierLocation.supplierCountry &&
			this.websiteURL
		);
	}

	updateCompanyInfo(updateData: IUpdateFromCompanyInfoPage): void {
		this.primaryContact.primaryContactName = updateData.primaryContactName;
		this.primaryContact.primaryContactTitle = updateData.primaryContactTitle;
		this.primaryContact.primaryContactPhone = updateData.primaryContactPhone;

		this.supplierName = updateData.supplierName;
		this.supplierLocation.supplierAddress1 = updateData.supplierAddress1;
		this.supplierLocation.supplierAddress2 = updateData.supplierAddress2;
		this.supplierLocation.supplierCity = updateData.supplierCity;
		this.supplierLocation.supplierFax = updateData.supplierFax;
		this.supplierLocation.supplierPhone = updateData.supplierPhone;
		this.supplierLocation.supplierState = updateData.supplierState;
		this.supplierLocation.supplierZip = updateData.supplierZip;
		this.supplierLocation.supplierCountry = updateData.supplierCountry;
		this.websiteURL = updateData.websiteURL;
	}

	updateProp65Info(updateData: IUpdateProp65Info): void {
		this.prop65Contact.prop65ContactName = updateData.prop65ContactName;
		this.prop65Contact.prop65ContactTitle = updateData.prop65ContactTitle;
		this.prop65Contact.prop65ContactPhone = updateData.prop65ContactPhone;
	}
}

export interface IUpdateProp65Info {
	prop65ContactName: string;
	prop65ContactTitle: string;
	prop65ContactPhone: string;
}

export interface IUpdateFromCompanyInfoPage {
	supplierName: string;
	supplierAddress1: string;
	supplierAddress2: string;
	supplierCity: string;
	supplierFax: string;
	supplierPhone: string;
	supplierState: string;
	supplierZip: string;
	supplierCountry: string;
	websiteURL: string;
	supplierId: string;
	primaryContactName: string;
	primaryContactTitle: string;
	primaryContactPhone: string;
	prop65ContactName: string;
	prop65ContactPhone: string;
	prop65ContactTitle: string;
}

export interface IUpdateDisplayPreferencesDTO {
	supplierId?: string;
	allowSellInCalifornia: boolean;
	allowNoOnProductWarning: boolean;
	allowUseShortWarning: boolean;
}

//CHANGE THIS
export interface ISupplierDTO {
	supplierId: string;
	supplierName: string;
	supplierAddress1: string;
	supplierAddress2?: string;
	supplierCity: string;
	supplierState: string;
	supplierCountry: string;
	supplierZip: string;
	supplierPhone: string;
	supplierFax?: string;
	primaryContactName: string;
	primaryContactTitle: string;
	primaryContactEmail: string;
	primaryContactPhone: string;
	prop65ContactName: string;
	prop65ContactTitle: string;
	prop65ContactEmail: string;
	prop65ContactPhone: string;
	websiteURL: string;
	currentUser?: string;
	allowSellInCalifornia?: boolean;
	allowNoOnProductWarning?: boolean;
	allowUseShortWarning?: boolean;
	primaryUsername?: string;
	prop65Username?: string;
	updatedBy?: string;
	updated?: string;
	hasItems?: boolean;
}
export interface LinkedAccount {
	address: string;
	city: string;
	fax: string;
	phone: string;
	allowNoOnProductWarning: boolean;
	allowUseShortWarning: boolean;
	state: string;
	supplierId: string;
	vendorId: string;
	vendorName: string;
	vendorNumber: string;
	zip: number;
}
